/**
 * Отрисовка эмодзи картинками вместо системного шрифта.
 *
 * ЗАЧЕМ. Системный шрифт рисует значки по-своему на каждой машине: у Максима
 * одно, у сотрудника с телефона другое, часть новых значков вообще квадратиками.
 * Картинки одинаковы у всех и выглядят опрятнее (Максим, 04.09.2026:
 * «а что красивых и анимированных нету?»).
 *
 * ⚠️ В БАЗЕ ПО-ПРЕЖНЕМУ ЮНИКОД. Подменяется только отрисовка: сообщение
 * остаётся обычным текстом, его можно скопировать, найти поиском и прочитать в
 * любом другом месте портала. Никаких служебных вставок вроде «:fire:» в тексте
 * нет — иначе они полезли бы в уведомления, выгрузки и поиск.
 *
 * Картинки лежат у нас (`public/emoji/tw`, набор Twemoji, CC-BY 4.0) — на
 * посторонние сервисы за ними никто не ходит.
 */

import React from "react";
import { ЕСТЬ_АНИМАЦИЯ } from "@/lib/emoji-anim";

/** Границы эмодзи: сам значок, модификаторы тона, склейки ZWJ, флаги, «клавиши». */
const ЭМОДЗИ_RE =
  /(\p{RI}\p{RI}|(?:\p{Emoji}️?⃣)|(?:\p{Extended_Pictographic}(?:️|\p{Emoji_Modifier})?(?:‍\p{Extended_Pictographic}(?:️|\p{Emoji_Modifier})?)*))/gu;

/** Имя файла: кодовые точки через дефис, «fe0f» отбрасываем — так назван набор. */
export function имяФайла(символ: string): string {
  const точки = [...символ].map((ch) => ch.codePointAt(0)!.toString(16));
  const без = точки.filter((p) => p !== "fe0f");
  return (без.length ? без : точки).join("-");
}

export const путьКартинки = (символ: string) => `/emoji/tw/${имяФайла(символ)}.svg`;

/**
 * Заменить эмодзи в строке на картинки.
 * @param размер высота значка в пикселях (по умолчанию — как строчная буква рядом)
 */
export function сЭмодзи(текст: string, ключ: string, размер = 18): React.ReactNode[] {
  const части: React.ReactNode[] = [];
  let хвост = 0, i = 0, m: RegExpExecArray | null;
  ЭМОДЗИ_RE.lastIndex = 0;
  while ((m = ЭМОДЗИ_RE.exec(текст)) !== null) {
    const символ = m[0];
    // Цифры и решётка попадают в \p{Emoji}: без «клавишного» хвоста это не значок.
    if (/^[0-9#*]$/.test(символ)) continue;
    if (m.index > хвост) части.push(текст.slice(хвост, m.index));
    части.push(
      <img key={`${ключ}-э-${i++}`} src={путьКартинки(символ)} alt={символ} draggable={false}
        style={{ width: размер, height: размер, display: "inline-block", verticalAlign: "-0.18em", margin: "0 0.5px" }}
        // Значка нет в наборе — показываем системный, а не пустую рамку.
        onError={(e) => {
          const el = e.currentTarget;
          const сам = document.createElement("span");
          сам.textContent = символ;
          el.replaceWith(сам);
        }} />
    );
    хвост = m.index + символ.length;
  }
  if (хвост < текст.length) части.push(текст.slice(хвост));
  return части.length ? части : [текст];
}

/** Сообщение состоит только из эмодзи (1–3 штуки) — тогда рисуем крупно и с анимацией. */
export function толькоЭмодзи(текст: string): string[] | null {
  const t = текст.trim();
  if (!t || t.length > 24) return null;
  const найдено = t.match(ЭМОДЗИ_RE) || [];
  const без = t.replace(ЭМОДЗИ_RE, "").trim();
  if (без !== "" || найдено.length === 0 || найдено.length > 3) return null;
  return найдено;
}

/** Крупные значки для сообщения из одних эмодзи: анимированные, если такие есть. */
export function КрупныеЭмодзи({ символы }: { символы: string[] }) {
  const размер = символы.length === 1 ? 64 : symbolSize(символы.length);
  return (
    <span className="flex items-center gap-1">
      {символы.map((с, i) => {
        const аним = ЕСТЬ_АНИМАЦИЯ[имяФайла(с)];
        return (
          <img key={i} src={аним ? `/emoji/anim/${имяФайла(с)}.webp` : путьКартинки(с)} alt={с} draggable={false}
            style={{ width: размер, height: размер }} />
        );
      })}
    </span>
  );
}

function symbolSize(n: number): number {
  return n === 2 ? 52 : 44;
}
